import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import { useEffect } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，您要查看的文章不存在或已被移除。" />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="mt-6 text-xl font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300"
            >
              返回首页
            </Link>
          </div>
        </main>
      </>
    );
  }

  const categoryName = categoryMap[article.category] || article.category;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      url: `https://91c.com/article/${article.slug}`,
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91c.com' },
        { '@type': 'ListItem', position: 2, name: categoryName, item: `https://91c.com/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://91c.com/article/${article.slug}`}
        ogType="article"
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogUrl={`https://91c.com/article/${article.slug}`}
        jsonLd={jsonLd}
      />

      <main className="pt-16 min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-8">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Link
                to={`/category/${article.category}`}
                className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
              >
                {categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400">
              <span>
                <i className="ri-user-line mr-1"></i>
                {article.author}
              </span>
              <span>
                <i className="ri-calendar-line mr-1"></i>
                {article.date}
              </span>
              <span>
                <i className="ri-time-line mr-1"></i>
                {article.readTime}阅读
              </span>
            </div>
          </header>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-1
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <footer className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground-800">
                  作者：{article.author}
                </p>
                <p className="text-xs text-foreground-400 mt-1">
                  发布于 {article.date} · {article.readTime}阅读
                </p>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="px-5 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors duration-200"
              >
                返回{categoryName}
              </Link>
            </div>
          </footer>
        </article>
      </main>
    </>
  );
}